// Enhanced StorytellerView.jsx with typing indicators and sound effects
// Replace the entire StorytellerView component with this version
// ✨ UPDATED: Color theme changed from yellow highlights to black/white/gray highlights.

import React, { useState, useEffect, useRef } from 'react';
import { FaBookOpen, FaGlobe, FaStar, FaHandPaper, FaHandshake, FaKey } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { soundManager } from '../../../utils/soundUtils'; // ✨ NEW: Import soundManager

const icons = {
  'Nominativ': <FaStar />,
  'Akkusativ': <FaHandPaper />,
  'Dativ': <FaHandshake />,
  'Genitiv': <FaKey />,
  'Partizip I as Adjective (Active)': <FaBookOpen />,
  'Partizip II as Adjective (Passive)': <FaBookOpen />,
  'Gerundiv (zu + Partizip I)': <FaBookOpen />,
};

// ✨ NEW: Typing Indicator Component
const TypingIndicator = ({ speakerRole, speakerName, isMobile }) => {
  const isSpeakerA = speakerRole === 'A';

  const containerStyle = {
    opacity: 0,
    animation: 'fadeInUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
    marginBottom: isMobile ? '1rem' : '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  };

  const bubbleStyle = {
    maxWidth: isMobile ? '80%' : '75%',
    width: 'fit-content',
    padding: isMobile ? '0.875rem 1rem' : '1rem 1.25rem',
    borderRadius: '1.25rem',
    position: 'relative',
    marginBottom: isMobile ? '0.5rem' : '0.5rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    fontSize: isMobile ? '0.9375rem' : '1rem',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.75rem',
    backgroundColor: isSpeakerA ? '#FFFFFF' : '#3A86FF',
    border: isSpeakerA ? '2px solid #E5E7EB' : '2px solid #3A86FF',
    alignSelf: isSpeakerA ? 'flex-start' : 'flex-end',
  };

  const speakerStyle = {
    fontWeight: '700',
    fontSize: isMobile ? '0.8125rem' : '0.875rem',
    color: isSpeakerA ? '#000000' : '#FFFFFF',
    letterSpacing: '0.01em',
  };

  const dotStyle = {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: isSpeakerA ? '#3A86FF' : '#FFFFFF',
    animation: 'typingDot 1.4s infinite',
  };

  return (
    <div style={containerStyle}>
      <div style={bubbleStyle}>
        <span style={speakerStyle}>{speakerName}</span>
        <span style={{ ...dotStyle, animationDelay: '0s' }} />
        <span style={{ ...dotStyle, animationDelay: '0.2s' }} />
        <span style={{ ...dotStyle, animationDelay: '0.4s' }} />
      </div>
    </div>
  );
};

// GrammarInsightCard component (unchanged, uses blue theme)
const GrammarInsightCard = ({ insight, isMobile }) => {
  const styles = {
    card: {
      backgroundColor: '#FFFFFF',
      border: '2px solid #E5E7EB',
      borderRadius: '1rem',
      padding: isMobile ? '1rem' : '1.25rem',
      marginTop: '1rem',
      marginLeft: isMobile ? 0 : '1rem',
      maxWidth: isMobile ? '100%' : 'calc(100% - 1rem)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      display: 'flex',
      gap: '1rem',
      alignItems: 'flex-start',
      animation: 'popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
      opacity: 0,
      transform: 'scale(0.95)',
      transition: 'transform 0.2s ease',
    },
    icon: {
      fontSize: '1.75rem',
      color: '#3A86FF',
      flexShrink: 0,
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#F0F4FF',
      borderRadius: '0.5rem',
    },
    title: {
      fontWeight: '700',
      color: '#000000',
      fontSize: '0.9375rem',
      marginBottom: '0.5rem',
    },
    explanation: {
      fontSize: '0.875rem',
      color: '#374151',
      lineHeight: '1.6',
    },
    rule: {
      fontSize: '0.8125rem',
      color: '#3A86FF',
      backgroundColor: '#F0F4FF',
      padding: '0.375rem 0.75rem',
      borderRadius: '0.5rem',
      marginTop: '0.75rem',
      display: 'inline-block',
      fontWeight: '600',
      border: '1px solid #D1E0FF',
    }
  };
  return (
    <div style={styles.card}>
      <div style={styles.icon}>{insight.icon}</div>
      <div>
        <div style={styles.title}>{insight.grammar_point}</div>
        <div style={styles.explanation}>{insight.explanation}</div>
        {insight.rule && <div style={styles.rule}>{insight.rule}</div>}
      </div>
    </div>
  );
};

// InteractiveDialogueLine component (✨ UPDATED HIGHLIGHT COLORS)
const InteractiveDialogueLine = ({ lineData, speakerRole, isMobile }) => {
  const { t } = useTranslation();
  const [showTranslation, setShowTranslation] = useState(false);
  const [showNotes, setShowNotes] = useState({});
  const [hoveredGroup, setHoveredGroup] = useState(null);
      const processText = (text) => {
    if (typeof text !== 'string') return text;
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, index) => {
      return index % 2 === 1 ? <strong key={index} style={{ fontWeight: '700' }}>{part}</strong> : part;
    });
  };
  let speaker = '', dialogue = '', translation = '', notes = [];
  if (typeof lineData === 'object' && lineData !== null) {
    speaker = lineData.speaker || '';
    dialogue = lineData.dialogue || '';
    translation = lineData.translation || '';
    notes = lineData.notes || [];
  } else {
    dialogue = String(lineData);
  }

  const toggleNote = (index) => {
    setShowNotes(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const styles = {
  lineContainer: {
    opacity: 0,
    animation: 'fadeInUp 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
    marginBottom: isMobile ? '1rem' : '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  bubble: {
    maxWidth: isMobile ? '80%' : '75%',
    width: 'fit-content',
    padding: isMobile ? '0.875rem 1rem' : '1rem 1.25rem',
    borderRadius: '1.25rem',
    lineHeight: '1.5',
    position: 'relative',
    marginBottom: isMobile ? '0.5rem' : '0.5rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'pre-wrap',
    fontSize: isMobile ? '0.9375rem' : '1rem',
    display: 'inline-block',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  speaker: {
    fontWeight: '700',
    marginBottom: '0.375rem',
    display: 'block',
    fontSize: isMobile ? '0.8125rem' : '0.875rem',
    letterSpacing: '0.01em',
  },
  speakerABubble: {
    backgroundColor: '#FFFFFF',
    border: '2px solid #E5E7EB',
    color: '#000000',
    borderBottomLeftRadius: '0.375rem',
    alignSelf: 'flex-start',
  },
  speakerBBubble: {
    backgroundColor: '#3A86FF', // Royal Blue
    color: '#FFFFFF',
    border: '2px solid #3A86FF',
    borderBottomRightRadius: '0.375rem',
    alignSelf: 'flex-end',
  },
   highlight: {
  backgroundColor: 'rgba(255, 255, 255, 0.3)',
  color: '#000000',
  padding: '0.15rem 0.4rem',
  borderRadius: '0.375rem',
  cursor: 'pointer',
  borderBottom: '2px solid rgba(0, 0, 0, 0.3)',
  transition: 'all 0.2s ease',
  fontWeight: '600',
},
highlightActive: {
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  color: '#000000',
  padding: '0.15rem 0.4rem',
  borderRadius: '0.375rem',
  cursor: 'pointer',
  borderBottom: '2px solid #000000',
  transition: 'all 0.2s ease',
  fontWeight: '700',
  transform: 'translateY(-1px)',
},
    translation: { fontSize: '0.9rem', color: '#64748b', fontStyle: 'italic', marginTop: '0.5rem' },
    actionBar: { display: 'flex', gap: '1rem', marginTop: '0.5rem' },
    actionIcon: { display: 'inline-flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer', color: '#6b7280', fontSize: '0.85rem' }
  };

  const highlightText = (text, textNotes) => {
    if (!text || !textNotes || textNotes.length === 0) {
      // No notes to highlight, but still process bold markdown
      return processText(text);
    }

    // First process bold markdown, then handle highlights
    const sortedNotes = [...textNotes].sort((a, b) => (b.highlight || '').length - (a.highlight || '').length);
    let parts = [text];

    sortedNotes.forEach(note => {
      const originalNoteIndex = textNotes.indexOf(note);
      if (!note.highlight) return;
      let newParts = [];
      parts.forEach(part => {
        if (typeof part !== 'string') {
          newParts.push(part);
          return;
        }
        if (note.highlight.includes('...')) {
          const highlightSegments = note.highlight.split('...').map(s => s.trim().replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'));
          const regex = new RegExp(`(${highlightSegments[0]})(.*?)(${highlightSegments[1]})`, 'g');
          let lastIndex = 0;
          let match;
          while ((match = regex.exec(part)) !== null) {
            if (match.index > lastIndex) {
              newParts.push(part.substring(lastIndex, match.index));
            }
            const spanStyle = hoveredGroup === originalNoteIndex ? styles.highlightActive : styles.highlight;
            newParts.push(
  <span
    key={`${match[1]}-${match.index}`}
    style={{
      ...spanStyle,
      color: '#000000',
    }}
    onClick={() => toggleNote(originalNoteIndex)}
    onMouseEnter={() => setHoveredGroup(originalNoteIndex)}
    onMouseLeave={() => setHoveredGroup(null)}
  >
    {processText(match[1])} <FaBookOpen size={10} style={{display: 'inline', marginLeft: '2px', color: '#000000'}}/>
  </span>
);
            newParts.push(processText(match[2]));
            newParts.push(
  <span
    key={`${match[3]}-${match.index}`}
    style={{
      ...spanStyle,
      color: '#000000',
    }}
    onClick={() => toggleNote(originalNoteIndex)}
    onMouseEnter={() => setHoveredGroup(originalNoteIndex)}
    onMouseLeave={() => setHoveredGroup(null)}
  >
    {processText(match[3])} <FaBookOpen size={10} style={{display: 'inline', marginLeft: '2px', color: '#000000'}}/>
  </span>
);
            lastIndex = match.index + match[0].length;
          }
          if (lastIndex < part.length) {
            newParts.push(part.substring(lastIndex));
          }
        } else {
          const splitParts = part.split(note.highlight);
          for (let i = 0; i < splitParts.length; i++) {
            newParts.push(processText(splitParts[i]));
            if (i < splitParts.length - 1) {
              newParts.push(
                <span
  key={`${note.highlight}-${i}`}
  style={{
    ...styles.highlight,
    color: '#000000', // Force black text
  }}
  onClick={() => toggleNote(originalNoteIndex)}
>
  {processText(note.highlight)} <FaBookOpen size={10} style={{display: 'inline', marginLeft: '2px', color: '#000000'}}/>
</span>
              );
            }
          }
        }
      });
      parts = newParts;
    });
    return parts;
  };

  const isSpeakerA = speakerRole === 'A';

  const alignmentWrapperStyle = {
    maxWidth: isMobile ? '80%' : '90%',
    width: '100%',
    ...(isSpeakerA
      ? { marginRight: 'auto', paddingLeft: isMobile ? '0.5rem' : '1rem', textAlign: 'left' }
      : { marginLeft: 'auto', paddingRight: isMobile ? '0.5rem' : '1rem', textAlign: 'right' }
    )
  };

  return (
    <div style={styles.lineContainer}>
      <div style={{ ...styles.bubble, ...(isSpeakerA ? styles.speakerABubble : styles.speakerBBubble) }}>
        <span style={{ ...styles.speaker, color: isSpeakerA ? '#000000' : '#FFFFFF' }}>{speaker}</span>
        {highlightText(dialogue, notes)}
      </div>

      <div style={alignmentWrapperStyle}>
        {translation && (
          <div style={styles.actionBar}>
            <span style={styles.actionIcon} onClick={() => setShowTranslation(!showTranslation)}>
              <FaGlobe /> {showTranslation ? t("vocabulary.card.hideTranslation") : t("vocabulary.card.tapForTranslation")}
            </span>
          </div>
        )}
        {showTranslation && <div style={styles.translation}>{translation}</div>}
      </div>

      {notes?.map((note, index) => showNotes[index] && (
        <GrammarInsightCard key={index} insight={{...note.insight, icon: icons[note.insight.grammar_point]}} isMobile={isMobile} />
      ))}
    </div>
  );
};

// ✨ ENHANCED: StorytellerView (Functionality unchanged, colors updated in child)
const StorytellerView = ({ pillar, onStoryComplete, isMobile }) => {
  console.log('📱 StorytellerView isMobile:', isMobile);
  const [visibleLines, setVisibleLines] = useState([]);
  const [typingIndicators, setTypingIndicators] = useState([]); // ✨ NEW: Track typing indicators
  const [isStoryComplete, setIsStoryComplete] = useState(false);
  const onStoryCompleteRef = useRef(onStoryComplete);
  const timeoutsRef = useRef([]);
  const pillarIdRef = useRef(null);

  const storyTitle = pillar?.context?.title;
  const storyLines = pillar?.context?.lines || [];
  const uniqueSpeakers = [...new Set(storyLines.map(line => line.speaker).filter(Boolean))];
  const speakerRoleMap = uniqueSpeakers.reduce((acc, speakerName, index) => {
    if (index === 0) acc[speakerName] = 'A';
    if (index === 1) acc[speakerName] = 'B';
    return acc;
  }, {});

  useEffect(() => {
    onStoryCompleteRef.current = onStoryComplete;
  });

  // ✨ NEW: Add typing dot animation to stylesheet
  useEffect(() => {
    const styleId = 'storyteller-animations';
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement("style");
      styleSheet.id = styleId;
      styleSheet.innerText = `
        @keyframes fadeInUp { 
  from { 
    opacity: 0; 
    transform: translateY(30px); 
  } 
  to { 
    opacity: 1; 
    transform: translateY(0); 
  } 
}
@keyframes popIn { 
  from { 
    opacity: 0; 
    transform: scale(0.85); 
  } 
  to { 
    opacity: 1; 
    transform: scale(1); 
  } 
}
@keyframes typingDot {
  0%, 60%, 100% { 
    transform: translateY(0) scale(1); 
    opacity: 0.5;
  }
  30% { 
    transform: translateY(-8px) scale(1.2); 
    opacity: 1;
  }
}
      `;
      document.head.appendChild(styleSheet);
    }

    const currentPillarId = pillar?.id;
    if (pillarIdRef.current !== currentPillarId || !isStoryComplete) {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
      setVisibleLines([]);
      setTypingIndicators([]); // ✨ NEW: Reset typing indicators
      setIsStoryComplete(false);
      pillarIdRef.current = currentPillarId;

      if (storyLines.length === 0) {
        if (onStoryCompleteRef.current) onStoryCompleteRef.current();
        return;
      }

      // ✨ ENHANCED: Dynamic timing based on message length
      let cumulativeDelay = 2000; // Start after 2 seconds

      storyLines.forEach((line, index) => {
        const typingDelay = cumulativeDelay;

        // Calculate typing indicator duration based on message length
        const messageText = line.dialogue || '';
        const messageLength = messageText.length;

        // Dynamic typing indicator display: minimum 2 seconds, scales with message length
        // Short messages (< 50 chars) = 2 seconds typing
        // Medium messages (50-100 chars) = 3 seconds typing
        // Long messages (100-150 chars) = 4 seconds typing
        // Very long messages (> 150 chars) = 5-6 seconds typing
        let typingIndicatorDuration;
        if (messageLength < 50) {
          typingIndicatorDuration = 2000; // 2 seconds
        } else if (messageLength < 100) {
          typingIndicatorDuration = 3000; // 3 seconds
        } else if (messageLength < 150) {
          typingIndicatorDuration = 4000; // 4 seconds
        } else if (messageLength < 200) {
          typingIndicatorDuration = 5000; // 5 seconds
        } else {
          typingIndicatorDuration = 6000; // 6 seconds for very long messages
        }

        // Reading time after message appears (4-10 seconds based on length)
        let readingDuration;
        if (messageLength < 50) {
          readingDuration = 4000; // 4 seconds
        } else if (messageLength < 100) {
          readingDuration = 5000; // 5 seconds
        } else if (messageLength < 150) {
          readingDuration = 6000; // 6 seconds
        } else if (messageLength < 200) {
          readingDuration = 7000; // 7 seconds
        } else if (messageLength < 250) {
          readingDuration = 8000; // 8 seconds
        } else {
          readingDuration = 10000; // 10 seconds for very long messages
        }

        const messageDelay = typingDelay + typingIndicatorDuration; // Show typing indicator based on message length

        // Show typing indicator
        const typingTimeout = setTimeout(() => {
          setTypingIndicators(prev => [...prev, index]);
        }, typingDelay);
        timeoutsRef.current.push(typingTimeout);

        // Show actual message and hide typing indicator
        const messageTimeout = setTimeout(() => {
          setTypingIndicators(prev => prev.filter(i => i !== index));
          setVisibleLines(prev => [...prev, line]);

          // ✨ NEW: Play sound when message appears
          // Speaker A (first speaker) = receive sound
          // Speaker B (second speaker) = send sound
          const speakerRole = speakerRoleMap[line.speaker];
          if (speakerRole === 'A') {
            soundManager.play('/sfx/receive.mp3', 0.5); // Receive sound for first speaker
          } else if (speakerRole === 'B') {
            soundManager.play('/sfx/send.mp3', 0.5); // Send sound for second speaker
          }
        }, messageDelay);
        timeoutsRef.current.push(messageTimeout);

        // Update cumulative delay for next message
        // Add typing indicator time + message reading time
        cumulativeDelay = messageDelay + readingDuration;
      });

      // Story completion
      const completionTimeout = setTimeout(() => {
        setIsStoryComplete(true);
        if (onStoryCompleteRef.current) onStoryCompleteRef.current();
      }, cumulativeDelay + 500); // Add small buffer
      timeoutsRef.current.push(completionTimeout);
    }

    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [pillar?.id, isStoryComplete, storyLines]);

  const styles = {
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: isMobile ? '1rem' : '1.5rem',
    padding: isMobile ? '1.5rem' : '2rem',
    border: '2px solid #000000',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    maxWidth: '100%',
    overflowX: 'hidden',
    position: 'relative',
  },
  storyTitle: {
    fontSize: isMobile ? '1.25rem' : '1.5rem',
    fontWeight: '700',
    color: '#000000',
    paddingBottom: '1rem',
    marginBottom: '1.5rem',
    borderBottom: '2px solid #E5E7EB',
    textAlign: 'center',
    letterSpacing: '-0.01em',
  }
};

  if (storyLines.length === 0) {
    return <div style={styles.container}><p>No story available for this topic.</p></div>
  }

  return (
    <div style={styles.container}>
      {storyTitle && <h3 style={styles.storyTitle}>{storyTitle}</h3>}

      {/* ✨ NEW: Render visible lines and typing indicators */}
      {storyLines.map((lineData, index) => {
        const isVisible = visibleLines.includes(lineData);
        const isTyping = typingIndicators.includes(index);

        if (isTyping) {
          // Show typing indicator with speaker name
          return (
            <TypingIndicator
              key={`typing-${index}`}
              speakerRole={speakerRoleMap[lineData.speaker] || 'A'}
              speakerName={lineData.speaker} // ✨ ADDED: Pass speaker name
              isMobile={isMobile}
            />
          );
        } else if (isVisible) {
          // Show actual message
          return (
            <InteractiveDialogueLine
              key={index}
              lineData={lineData}
              speakerRole={speakerRoleMap[lineData.speaker] || 'A'}
              isMobile={isMobile}
            />
          );
        }
        return null;
      })}
    </div>
  );
};

export default StorytellerView;